/**
 * Fair Home V2 — wheel arbitration between the pager and the slides' own
 * scrolling.
 *
 * Cooldown: a trackpad flick's momentum outlives the ~520ms pager transition,
 * and its tail is jagged (… 6 6 10 …, … 32 168 25 …). Swiper pages again on any
 * delta that grows, so one swipe used to skip a section. Telling momentum from
 * a new swipe by the SHAPE of the stream proved fragile on real Mac logs; by its
 * TIMING it is not: momentum is one continuous stream, a new swipe starts after a
 * pause (`SWIPE_GAP_MS`). So after a page turn the wheel is held through the
 * transition and then until the stream pauses (T-028 — a flat 1 s cooldown also
 * swallowed a deliberate second swipe).
 *
 * FE-004: on short viewports (`@media (max-height: 860px)`, see
 * `fair-home-v2.css`) a slide the slide-fit zoom could not fit becomes
 * scrollable. The wheel is kept from Swiper while the slide has room left to
 * scroll in that direction; a stream that was scrolling the slide does not page
 * on reaching the edge — the next swipe does.
 */
import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue'
import { FAIR_HOME_V2_PAGER_SPEED_MS } from './fair-home-v2-scroll.data'

/** Sub-pixel slack: scrollHeight/clientHeight rounding must not fake an edge. */
const EDGE_EPSILON_PX = 1

/** The page transition itself: the wheel never reaches Swiper while it runs. */
const PAGE_TRANSITION_GUARD_MS = FAIR_HOME_V2_PAGER_SPEED_MS + 50

/** The longest a trackpad momentum tail is waited out after a page turn. */
const PAGE_MOMENTUM_MAX_MS = 2000

/** A pause longer than this between wheel events ends a swipe (Swiper uses 150ms too). */
const SWIPE_GAP_MS = 150

/** Slides overflowing by less than this are nudged, not read — their swipe may still page. */
const LATCH_MIN_OVERFLOW_PX = 64

export function useFairHomeV2SlideScroll(
  rootRef: Ref<HTMLElement | null>,
  activeSlide: Ref<number>,
) {
  let pagedAt = -Infinity
  let slideScrolledAt = -Infinity
  let lastWheelAt = -Infinity
  /** A pause in the wheel stream since the last page turn: what comes next is a new swipe. */
  let gapSincePage = true

  // Any page turn (wheel, drag, keys) starts the cooldown.
  watch(
    activeSlide,
    () => {
      pagedAt = performance.now()
      gapSincePage = false
    },
    { flush: 'sync' },
  )

  function onWheelCapture(event: WheelEvent) {
    // T-028 — a flat 1 s cooldown ate a second swipe made right after the transition, so it
    // took two swipes to move on. Now the wheel is held only while the transition runs and
    // while the SAME stream (the momentum tail) keeps coming; a pause ends it, and the next
    // swipe pages at once.
    if (event.timeStamp - lastWheelAt >= SWIPE_GAP_MS) gapSincePage = true
    lastWheelAt = event.timeStamp
    const sincePage = event.timeStamp - pagedAt
    // Native scrolling stays in every branch below — only Swiper is kept out.
    if (
      sincePage < PAGE_TRANSITION_GUARD_MS
      || (!gapSincePage && sincePage < PAGE_MOMENTUM_MAX_MS)
    ) {
      event.stopPropagation()
      return
    }

    const target = event.target as HTMLElement | null
    const scrollport = target?.closest<HTMLElement>('.fair-home-v2__slide-inner')
    if (!scrollport) return

    const overflow = scrollport.scrollHeight - scrollport.clientHeight
    if (overflow <= EDGE_EPSILON_PX) return

    const goingDown = event.deltaY > 0
    const atTop = scrollport.scrollTop <= EDGE_EPSILON_PX
    const atBottom = scrollport.scrollTop >= overflow - EDGE_EPSILON_PX
    const sameSwipe = event.timeStamp - slideScrolledAt <= SWIPE_GAP_MS

    // At the edge the pager pages — unless this very swipe was scrolling the slide.
    if ((goingDown ? atBottom : atTop) && !sameSwipe) return

    if (overflow >= LATCH_MIN_OVERFLOW_PX) slideScrolledAt = event.timeStamp
    event.stopPropagation()
  }

  onMounted(() => {
    rootRef.value?.addEventListener('wheel', onWheelCapture, {
      capture: true,
      passive: true,
    })
  })

  onBeforeUnmount(() => {
    rootRef.value?.removeEventListener('wheel', onWheelCapture, {
      capture: true,
    } as EventListenerOptions)
  })
}
